<script>
  import { link } from "svelte-routing";
  import FooterAdmin from "../components/Footers/FooterAdmin.svelte";
  import Logo from "../components/Logo.svelte";
</script>

<div class="bg-canvas min-h-screen">
  <!-- Simple Navbar -->
  <nav class="fixed top-0 z-50 w-full bg-surface border-b border-line">
    <div class="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
      <a use:link href="/" aria-label="Moira Finance home">
        <Logo />
      </a>
      <div class="flex items-center gap-2">
        <a use:link href="/terms" class="btn btn-ghost btn-sm">Terms</a>
        <a use:link href="/privacy" class="btn btn-ghost btn-sm">Privacy</a>
        <a use:link href="/auth/login" class="btn btn-outline btn-sm">
          Sign in
        </a>
      </div>
    </div>
  </nav>

  <main class="max-w-3xl mx-auto px-4 pt-28 pb-16">
    <h1 class="font-display text-4xl font-medium tracking-tight text-ink">Privacy Policy</h1>
    <p class="text-sm text-ink-tertiary mt-2">Last Updated: December 2024</p>

    <div class="text-base text-ink-secondary leading-relaxed">

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">1. Introduction</h2>
        <p class="mb-4">
          Moira Finance ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy
          explains how we collect, use, disclose, and safeguard your information when you use our Service.
        </p>
        <p>
          Please read this Privacy Policy carefully. By using our Service, you agree to the collection and
          use of information in accordance with this policy.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">2. Brokerage Service Privacy</h2>
        <p class="mb-4">
          In addition to this Privacy Policy, your brokerage account is subject to the Alpaca Securities LLC
          Privacy Notice, which can be found at:
        </p>
        <a
          href="https://files.alpaca.markets/disclosures/library/PrivacyNotice.pdf"
          target="_blank"
          rel="noopener"
          class="btn btn-outline btn-sm mb-4"
        >
          <i class="fas fa-file-pdf text-ink-tertiary"></i>
          Alpaca Securities Privacy Notice (PDF)
        </a>
        <p>
          The Alpaca Privacy Notice governs how your brokerage account information is collected, used,
          and shared by Alpaca Securities LLC.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">3. Information We Collect</h2>

        <h3 class="text-base font-semibold text-ink mt-6 mb-3">3.1 Information You Provide</h3>
        <p class="mb-3">We collect information that you provide directly to us, including:</p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li><strong class="text-ink">Account Information:</strong> Name, email address, phone number when you create an account</li>
          <li><strong class="text-ink">Identity Verification:</strong> Social Security Number, date of birth, address, and other information required for regulatory compliance</li>
          <li><strong class="text-ink">Financial Information:</strong> Employment status, annual income, net worth, investment experience, and funding source</li>
          <li><strong class="text-ink">Investment Preferences:</strong> Risk tolerance, investment goals, and portfolio allocations</li>
          <li><strong class="text-ink">Communications:</strong> Any messages, feedback, or requests you send to us</li>
        </ul>

        <h3 class="text-base font-semibold text-ink mt-6 mb-3">3.2 Information Collected Automatically</h3>
        <p class="mb-3">When you use our Service, we automatically collect:</p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li><strong class="text-ink">Device Information:</strong> Browser type, operating system, device identifiers</li>
          <li><strong class="text-ink">Usage Data:</strong> Pages visited, features used, time spent on pages</li>
          <li><strong class="text-ink">IP Address:</strong> Your Internet Protocol address and approximate geographic location</li>
          <li><strong class="text-ink">Cookies and Tracking:</strong> Information collected through cookies, web beacons, and similar technologies</li>
        </ul>

        <h3 class="text-base font-semibold text-ink mt-6 mb-3">3.3 Information from Third Parties</h3>
        <p class="mb-3">We may receive information about you from:</p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li><strong class="text-ink">Google OAuth:</strong> Email address and profile information when you sign in with Google</li>
          <li><strong class="text-ink">Alpaca Securities:</strong> Account status, trading data, and account balances</li>
          <li><strong class="text-ink">Plaid (if applicable):</strong> Bank account information for ACH transfers</li>
          <li><strong class="text-ink">Identity Verification Services:</strong> Results of identity verification checks</li>
        </ul>

        <h3 class="text-base font-semibold text-ink mt-6 mb-3">3.4 Google User Data</h3>
        <p class="mb-3">
          When you sign in using Google OAuth, we access and collect the following information from your Google account:
        </p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li><strong class="text-ink">Email Address:</strong> Your primary Google account email address</li>
          <li><strong class="text-ink">Profile Information:</strong> Your name and profile picture (if publicly available)</li>
          <li><strong class="text-ink">Google ID:</strong> A unique identifier associated with your Google account</li>
        </ul>
        <p class="mb-3"><strong class="text-ink">How We Use Google User Data:</strong></p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li><strong class="text-ink">Authentication:</strong> To verify your identity and allow you to sign in to our Service</li>
          <li><strong class="text-ink">Account Creation:</strong> To create and maintain your Moira Finance account</li>
          <li><strong class="text-ink">Communication:</strong> To send you important account-related notifications to your email address</li>
          <li><strong class="text-ink">Personalization:</strong> To display your name and profile picture within the Service</li>
        </ul>
        <p class="mb-3"><strong class="text-ink">How We Store Google User Data:</strong></p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li>Your Google email, name, profile picture URL, and Google ID are stored securely in our database</li>
          <li>Data is encrypted at rest and in transit using industry-standard encryption (TLS/SSL)</li>
          <li>Access to Google user data is restricted to authorized personnel only</li>
          <li>We retain this data for as long as your account remains active</li>
        </ul>
        <p class="mb-3"><strong class="text-ink">How We Share Google User Data:</strong></p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li>We do <strong class="text-ink">not</strong> sell your Google user data to third parties</li>
          <li>We do <strong class="text-ink">not</strong> share your Google user data with third parties for advertising purposes</li>
          <li>Your email may be shared with Alpaca Securities LLC solely for the purpose of managing your brokerage account</li>
          <li>We may disclose Google user data if required by law or to protect our legal rights</li>
        </ul>
        <p class="mb-4">
          <strong class="text-ink">Limited Use Disclosure:</strong> Moira Finance's use and transfer to any other app of information
          received from Google APIs will adhere to the
          <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener" class="text-moira hover:underline">
            Google API Services User Data Policy
          </a>, including the Limited Use requirements.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">4. How We Use Your Information</h2>
        <p class="mb-3">We use the information we collect to:</p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li>Provide, maintain, and improve our Service</li>
          <li>Process transactions and manage your brokerage account</li>
          <li>Verify your identity and comply with regulatory requirements (KYC/AML)</li>
          <li>Send you technical notices, updates, and support messages</li>
          <li>Respond to your comments, questions, and requests</li>
          <li>Monitor and analyze trends, usage, and activities</li>
          <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
          <li>Personalize and improve your experience</li>
          <li>Comply with legal obligations and regulatory requirements</li>
        </ul>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">5. Sharing of Information</h2>
        <p class="mb-3">We may share your information in the following circumstances:</p>

        <h3 class="text-base font-semibold text-ink mt-6 mb-3">5.1 Service Providers</h3>
        <p class="mb-3">
          We share information with third-party vendors, consultants, and service providers who need access
          to such information to carry out work on our behalf, including:
        </p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li><strong class="text-ink">Alpaca Securities LLC:</strong> For brokerage services and trade execution</li>
          <li><strong class="text-ink">Cloud hosting providers:</strong> For data storage and processing</li>
          <li><strong class="text-ink">Analytics providers:</strong> For usage analysis and improvement</li>
          <li><strong class="text-ink">Customer support tools:</strong> For providing customer service</li>
        </ul>

        <h3 class="text-base font-semibold text-ink mt-6 mb-3">5.2 Legal Requirements</h3>
        <p class="mb-3">We may disclose your information if required to do so by law or in response to:</p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li>Valid requests from law enforcement or government agencies</li>
          <li>Court orders, subpoenas, or other legal processes</li>
          <li>Requests from regulatory bodies (SEC, FINRA, etc.)</li>
          <li>Situations involving potential threats to the physical safety of any person</li>
        </ul>

        <h3 class="text-base font-semibold text-ink mt-6 mb-3">5.3 Business Transfers</h3>
        <p class="mb-4">
          If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your
          information may be transferred as part of that transaction. We will notify you via email and/or
          prominent notice on our Service of any change in ownership.
        </p>

        <h3 class="text-base font-semibold text-ink mt-6 mb-3">5.4 With Your Consent</h3>
        <p>
          We may share your information with third parties when you give us explicit consent to do so.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">6. Data Retention</h2>
        <p class="mb-3">
          We retain your personal information for as long as necessary to fulfill the purposes for which
          it was collected, including:
        </p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li>To provide our services to you</li>
          <li>To comply with legal obligations (securities regulations require retention of certain records for up to 7 years)</li>
          <li>To resolve disputes and enforce our agreements</li>
          <li>For legitimate business purposes</li>
        </ul>
        <p>
          When you close your account, we may retain certain information as required by law or for legitimate
          business purposes.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">7. Data Security</h2>
        <p class="mb-3">
          We implement appropriate technical and organizational security measures to protect your personal
          information, including:
        </p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li>Encryption of data in transit (TLS/SSL) and at rest</li>
          <li>Regular security assessments and penetration testing</li>
          <li>Access controls and authentication requirements</li>
          <li>Employee training on data protection</li>
          <li>Incident response procedures</li>
        </ul>
        <p>
          However, no method of transmission over the Internet or electronic storage is 100% secure.
          While we strive to use commercially acceptable means to protect your personal information,
          we cannot guarantee its absolute security.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">8. Your Rights and Choices</h2>

        <h3 class="text-base font-semibold text-ink mt-6 mb-3">8.1 Account Information</h3>
        <p class="mb-4">
          You may update, correct, or delete your account information at any time by logging into your
          account or contacting us. Note that some information may be retained as required by law or for
          legitimate business purposes.
        </p>

        <h3 class="text-base font-semibold text-ink mt-6 mb-3">8.2 Communications Preferences</h3>
        <p class="mb-4">
          You may opt out of receiving promotional communications from us by following the instructions
          in those messages. If you opt out, we may still send you non-promotional communications, such
          as those about your account or our ongoing business relations.
        </p>

        <h3 class="text-base font-semibold text-ink mt-6 mb-3">8.3 Cookies</h3>
        <p class="mb-4">
          Most web browsers are set to accept cookies by default. You can usually set your browser to
          remove or reject cookies. Please note that removing or rejecting cookies could affect the
          availability and functionality of our Service.
        </p>

        <h3 class="text-base font-semibold text-ink mt-6 mb-3">8.4 California Privacy Rights</h3>
        <p class="mb-3">
          If you are a California resident, you have additional rights under the California Consumer
          Privacy Act (CCPA), including:
        </p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li>The right to know what personal information we collect</li>
          <li>The right to request deletion of your personal information</li>
          <li>The right to opt-out of the sale of personal information (we do not sell personal information)</li>
          <li>The right to non-discrimination for exercising your privacy rights</li>
        </ul>
        <p>
          To exercise these rights, please contact us at
          <a href="mailto:privacy@moira.finance" class="text-moira hover:underline">privacy@moira.finance</a>.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">9. Children's Privacy</h2>
        <p>
          Our Service is not intended for anyone under the age of 18. We do not knowingly collect personal
          information from children under 18. If you are a parent or guardian and you are aware that your
          child has provided us with personal information, please contact us. If we become aware that we
          have collected personal information from children without verification of parental consent, we
          will take steps to remove that information from our servers.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">10. International Data Transfers</h2>
        <p>
          Our Service is operated in the United States. If you are located outside the United States,
          please be aware that information we collect will be transferred to, processed, and stored in
          the United States. By using our Service, you consent to the transfer of information to the
          United States, which may have different data protection rules than those of your country.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">11. Do Not Track Signals</h2>
        <p>
          Some browsers have a "Do Not Track" feature that signals to websites you visit that you do
          not want your online activity tracked. Our Service does not currently respond to "Do Not Track"
          signals. However, you can usually choose to set your browser to remove or reject cookies.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">12. Third-Party Links</h2>
        <p>
          Our Service may contain links to third-party websites and services. We are not responsible for
          the privacy practices of these third parties. We encourage you to read the privacy policies of
          any third-party sites you visit.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">13. Changes to This Privacy Policy</h2>
        <p class="mb-4">
          We may update this Privacy Policy from time to time. We will notify you of any changes by
          posting the new Privacy Policy on this page and updating the "Last Updated" date at the top.
        </p>
        <p>
          You are advised to review this Privacy Policy periodically for any changes. Changes to this
          Privacy Policy are effective when they are posted on this page. Your continued use of the
          Service after any changes constitutes your acceptance of the new Privacy Policy.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">14. Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
        </p>
        <div class="card card-pad mt-4">
          <p class="font-semibold text-ink">Moira Finance - Privacy Team</p>
          <p>Email: <a href="mailto:privacy@moira.finance" class="text-moira hover:underline">privacy@moira.finance</a></p>
          <p>General Support: <a href="mailto:support@moirafinance.com" class="text-moira hover:underline">support@moirafinance.com</a></p>
        </div>
      </section>

      <!-- Data Collection Summary Table -->
      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">15. Summary of Data Practices</h2>
        <div class="card overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead>
                <tr>
                  <th class="th">Category</th>
                  <th class="th">Data Collected</th>
                  <th class="th">Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="td">Identity</td>
                  <td class="td">Name, SSN, DOB, Address</td>
                  <td class="td">Account creation, KYC/AML compliance</td>
                </tr>
                <tr>
                  <td class="td">Contact</td>
                  <td class="td">Email, Phone</td>
                  <td class="td">Communications, Authentication</td>
                </tr>
                <tr>
                  <td class="td">Financial</td>
                  <td class="td">Income, Assets, Employment</td>
                  <td class="td">Regulatory compliance, Suitability</td>
                </tr>
                <tr>
                  <td class="td">Account</td>
                  <td class="td">Portfolios, Orders, Balances</td>
                  <td class="td">Service delivery</td>
                </tr>
                <tr>
                  <td class="td">Technical</td>
                  <td class="td">IP Address, Browser, Device</td>
                  <td class="td">Security, Analytics</td>
                </tr>
                <tr>
                  <td class="td">Usage</td>
                  <td class="td">Pages visited, Features used</td>
                  <td class="td">Improvement, Analytics</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

    </div>
  </main>

  <FooterAdmin />
</div>
